import { React, useState, useEffect, useMemo } from "react";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useMutation } from "@apollo/client";
import { useTheme } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";

import LoginImage from "../../../../../assets/images/delivery.png";

import {
  LOGIN,
  LOGIN_MFA_OPTIONS,
  REQUEST_PASSWORD,
  VALIDATE_MFA_LOGIN,
} from "../../../../../graphql/mutation/mutation";
import { useAuth } from "../../../../../context/Auth.context";

import {
  boxChild,
  signIn,
  forgetPassword,
  formControl,
  details,
} from "./../../Login.style";
import HocPopup from "../../../../../hoc/hocPopup";

import SelectMFAComponent from "./../../SelectMfa.component";
import MfaOTPComponent from "./../../MfaOTP.component";
import { InputValidation } from "../../../../../utils/InputValidationRegex";
import { TYPE } from "../../../../../utils/Utils";
import AppButton from "../../../../base/AppButton.component";
import InformationDialog from "../../../../base/dialog-body/Information.dialog";

const LoginBackup = ({ dialog }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const auth = useAuth();
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
    showPassword: true,
    emailError: "",
    passwordError: "",
  });

  const redirectPath = location.state?.path || "/";

  const [fetchLogin, { loading, data }] = useMutation(LOGIN, {
    onError: (err) => {
      console.log(err);
    },
  });

  const [
    requestPassword,
    { loading: requestingPassword, data: PasswordRequested },
  ] = useMutation(REQUEST_PASSWORD);

  const handleLogin = () => {
    if (
      (loginForm.email === "admin@echeck.com" &&
        loginForm.password === "Admin@1") ||
      (loginForm.email === "Admin@echeck.com" &&
        loginForm.password === "Admin@1")
    ) {
      auth.login({
        loggedInAs: "admin",
        dealership_logo:
          "https://s3.us-east-2.amazonaws.com/echeck.uat/app/images/dealership_logo/dealersure-logo.png",
        dealership_name: "Super Admin",
        dealership_user_id: 0,
        errors: null,
        first_name: "Super",
        last_name: "Admin",
      });
      navigate(redirectPath, {
        replace: true,
      });
    } else {
      fetchLogin({
        variables: {
          loginInput: {
            email: loginForm.email,
            password: loginForm.password,
            loginDevice: window?.innerWidth <= 750 ? "mobile" : "desktop",
          },
        },
      });
    }
  };

  useEffect(() => {
    if (data && data.login) {
      let errors = data.login.errors;
      if (errors) {
        if (
          errors[0].message === "User not exists. Please enter registered email"
        ) {
          setLoginForm({
            ...loginForm,
            emailError: "User not exists. Please enter registered email",
          });
        } else if (
          errors[0].message === "Incorrect password. Please check your password"
        ) {
          setLoginForm({
            ...loginForm,
            passwordError: "Incorrect password. Please check your password",
          });
        } else if (
          errors[0].message ===
          "Dealership Deactivated. Please contact Store Owner. "
        ) {
          setLoginForm({
            ...loginForm,
            emailError: "Dealership Deactivated. Please contact Store Owner",
          });
        } else if (
          errors[0].message === "User Deactivated. Please contact Store Owner. "
        ) {
          setLoginForm({
            ...loginForm,
            emailError: "User Deactivated. Please contact Store Owner. ",
          });
        } else if (
          errors[0].message ===
          "Your password has expired. Kindly reset it by clicking on the Forgot Password link."
        ) {
          setLoginForm({
            ...loginForm,
            emailError:
              "Your password has expired. Kindly reset it by clicking on the Forgot Password link.",
          });
        } else {
          setLoginForm({
            ...loginForm,
            emailError: "Unknown exception",
          });
        }
      } else {
        if (data.login.user_id) {
          data.login.loggedInAs = data.login.role;
          auth.login(data.login);
          navigate(redirectPath, {
            replace: true,
          });
          sessionStorage.setItem("jwt_token", data.login.token);
        }
      }
    }
  }, [data]);

  useEffect(() => {
    if (PasswordRequested?.requestPassword?.status) {
      dialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "Email sent",
        body: (
          <InformationDialog
            text={
              "Password reset link sent to your email. Please continue with the link to reset your password"
            }
          />
        ),
      });
    } else if (!PasswordRequested?.requestPassword?.status) {
      setLoginForm({
        ...loginForm,
        emailError: PasswordRequested?.requestPassword?.errors[0].message,
      });
    }
  }, [PasswordRequested]);

  const handleChange = (prop) => (event) => {
    setLoginForm({ ...loginForm, [prop]: event.target.value });
    let errorText;
    switch (prop) {
      case "email":
        if (InputValidation.emailValidation(event.target.value))
          errorText = "Invalid Email";
        if (event.target.value === "") errorText = "";
        setLoginForm({
          ...loginForm,
          email: event.target.value,
          emailError: errorText,
        });
        break;
      case "password":
        if (InputValidation.passwordValidation(event.target.value))
          errorText = "Invalid Password";
        if (event.target.value === "") errorText = "";
        setLoginForm({
          ...loginForm,
          password: event.target.value,
          passwordError: errorText,
        });
        break;
      default:
        break;
    }
  };

  const handleClickShowPassword = () => {
    setLoginForm({
      ...loginForm,
      showPassword: !loginForm.showPassword,
    });
  };

  return (
    <Box component="form" noValidate autoComplete="off" sx={boxChild}>
      <Box
        component="img"
        sx={{
          flow: 1,
          marginBottom: "40px",
          display: "flex",
          alignSelf: "center",
        }}
        alt="Login"
        src={LoginImage}
        width={180}
      />
      <Typography variant="h6" component="div" sx={signIn}>
        Sign in to DeliveryIQ+
      </Typography>
      <Typography variant="span" component="div" sx={details(theme)}>
        Enter your details below
      </Typography>

      <FormControl sx={formControl} variant="outlined">
        <InputLabel color={loginForm.emailError ? "error" : "primary"}>
          Email Address
        </InputLabel>
        <OutlinedInput
          label="Email Address"
          value={loginForm.email}
          onChange={handleChange("email")}
          sx={{ borderRadius: 2 }}
          error={loginForm.emailError}
        />
        <FormHelperText
          error={loginForm.emailError}
          sx={{ color: theme.palette.error.main }}
        >
          {loginForm.emailError || " "}
        </FormHelperText>
      </FormControl>
      <FormControl sx={formControl} variant="outlined">
        <InputLabel color={loginForm.emailError ? "error" : "primary"}>
          Password
        </InputLabel>
        <OutlinedInput
          label="Password"
          type={loginForm.showPassword ? "password" : "text"}
          value={loginForm.password}
          onChange={handleChange("password")}
          sx={{ borderRadius: 2 }}
          error={loginForm.passwordError}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={handleClickShowPassword}
                onMouseDown={(event) => event.preventDefault()}
                edge="end"
              >
                {loginForm.showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <FormHelperText
          error={loginForm.passwordError}
          sx={{ color: theme.palette.error.main }}
        >
          {loginForm.passwordError || " "}
        </FormHelperText>
      </FormControl>
      {requestingPassword ? (
        <CircularProgress sx={{ alignSelf: "center" }} />
      ) : (
        <AppButton
          variant="text"
          color="primary"
          sx={forgetPassword}
          buttonText=" Forgot Password?"
          disabled={requestingPassword}
          clickHandler={() => {
            if (loginForm.email === "") {
              setLoginForm({
                ...loginForm,
                emailError:
                  "Please enter your email address and then click on Forgot/Reset Password",
              });
            } else if (
              loginForm.email !== "" ||
              !InputValidation.emailValidation(loginForm.email)
            ) {
              requestPassword({
                variables: {
                  passwordInput: {
                    email: loginForm.email,
                  },
                },
              });
            }
          }}
        />
      )}
      {/* <SelectMFAComponent/> */}
      {/* <MfaOTPComponent/> */}
      <AppButton
        buttonText="Login"
        sx={{ m: 1 }}
        loading={loading}
        disabled={
          loginForm.email === "" ||
          loginForm.password === "" ||
          InputValidation.emailValidation(loginForm.email) ||
          InputValidation.passwordValidation(loginForm.password)
        }
        clickHandler={(event) => {
          loading ? event.preventDefault() : handleLogin();
        }}
      ></AppButton>
    </Box>
  );
};
export default HocPopup(LoginBackup);
